/**
 * PayWeekEngine (PR-05, Seth 2026-08-21: "Yes, payroll engine") — the ugly
 * Tech Hours Weekly spreadsheet, computed. Holly picks the pay week and the
 * engine pulls everything ESC already recorded:
 *
 *   hours    — proposed from ESC's own per-ticket tech hours (mirror_sales_emp
 *              reg+ot by invoice date). EDITABLE: a corrected cell is saved in
 *              Pegasus (pegasus_payroll_hours) and wins from then on — time
 *              cards beat proposals, per [[feedback_derive_dont_flag]].
 *   ticket $ — invoice dollars credited per tech per day (ESC's credit_pct
 *              when set, else split evenly across the ticket's techs).
 *   RHP      — (hourly + burden) x 1.75 from the ONE pay record, tech_pay
 *              (PAY-ONE-01: the same row Team Directory → a person → Pay &
 *              Commission edits; team.pay-gated; the sheet's exact formula,
 *              multiplier adjustable).
 *   %        — (week $ / week hours) / RHP. At or over the threshold (80%),
 *              bonus = bonus-pct (3%) x the week's ticket dollars.
 *   holidays — office-closed dates (org_setting payroll_holidays) tint their
 *              column and export those hours as HRS-HOL.
 *
 *   spiff    — Holly's daily-ticket tiers (2026-08-28): each DAY whose ticket
 *              dollars clear a band pays that band's % of THAT day's dollars
 *              ($1500-2499 = 1%, $2500-3499 = 2%, $3500+ = 3%; org_setting
 *              payroll_daily_spiff_tiers). Separate from the weekly bonus.
 *
 * Export = the ADP staging CSV in her entry order (REG/OT, HOL, PHONE, BONUS,
 * SPIFF). Honest gap stays hers: VAC/ESST come from her accrual ledgers until
 * the Pegasus accrual ledger ships (PAYROLL-ESST).
 */
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, HandCoins, Settings2, X } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { pagedAll } from "@/lib/pagedAll";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useOrgSettingNumber, useOrgSettingJson } from "@/lib/orgSettings";
import { TechAvatar } from "@/components/TechAvatar";
import { onErrToast } from "@/components/ui/mutationToast";
import { downloadCsv } from "./listCore";

const PSEUDO = new Set(["0000", "0001", "1000", "1001", "1002", "1003", "7777", "8888", "9999"]);

interface TechPayRow {
  empno: string;
  hourly: number | null;
  burden: number | null;
  rhp_multiplier: number;
  phone_stipend: number;
}

interface WeekTech {
  empno: string;
  name: string;
  /** iso day -> derived hours (ESC tickets) */
  derivedHours: Map<string, number>;
  /** iso day -> ticket dollars credited */
  dollars: Map<string, number>;
}

function isoOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function lastMonday(weeksBack = 1): Date {
  const now = new Date();
  const dow = (now.getDay() + 6) % 7; // Mon=0
  const thisMonday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow);
  return new Date(thisMonday.getFullYear(), thisMonday.getMonth(), thisMonday.getDate() - 7 * weeksBack);
}
function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}
function chunk<T>(arr: T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}
const usd = (v: number) => v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const usdExact = (v: number) => v.toLocaleString("en-US", { style: "currency", currency: "USD" });

function usePayWeek(mondayIso: string, sundayIso: string) {
  return useQuery({
    queryKey: ["pay_week", mondayIso],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<{ techs: WeekTech[] }> => {
      // 1) Invoices dated in the week.
      const sales = await pagedAll<{ invoice: string; inv_date: string | null; inv_amount: number | null }>(() =>
        supabase
          .from("mirror_sales")
          .select("invoice, inv_date, inv_amount")
          .gte("inv_date", mondayIso)
          .lte("inv_date", sundayIso)
          .order("invoice", { ascending: true }),
      );
      const byInvoice = new Map(sales.map((s) => [s.invoice, s]));
      const invoices = [...byInvoice.keys()];

      // 2) The techs on those invoices (hours + credit split).
      const emp: { invoice: string; empno: string | null; credit_pct: number | null; reg_hr: number | null; ot_hr: number | null }[] = [];
      for (const c of chunk(invoices, 200)) {
        const { data, error } = await supabase
          .from("mirror_sales_emp")
          .select("invoice, empno, credit_pct, reg_hr, ot_hr")
          .in("invoice", c);
        if (error) throw error;
        emp.push(...((data ?? []) as typeof emp));
      }
      const perInvoiceTechs = new Map<string, number>();
      for (const e of emp) {
        if (!e.empno || PSEUDO.has(e.empno)) continue;
        perInvoiceTechs.set(e.invoice, (perInvoiceTechs.get(e.invoice) ?? 0) + 1);
      }

      // 3) Names.
      const empnos = [...new Set(emp.map((e) => e.empno).filter((e): e is string => !!e && !PSEUDO.has(e)))];
      const { data: emps } = await supabase
        .from("mirror_employee")
        .select("empno, empname, firstname, lastname")
        .in("empno", empnos.length > 0 ? empnos : ["__none__"]);
      const names = new Map(
        ((emps ?? []) as { empno: string; empname: string | null; firstname: string | null; lastname: string | null }[]).map((e) => [
          e.empno,
          e.empname ?? `${e.firstname ?? ""} ${e.lastname ?? ""}`.trim() ?? e.empno,
        ]),
      );

      // 4) Fold per tech per day.
      const byTech = new Map<string, WeekTech>();
      for (const e of emp) {
        if (!e.empno || PSEUDO.has(e.empno)) continue;
        const s = byInvoice.get(e.invoice);
        if (!s?.inv_date) continue;
        const day = s.inv_date.slice(0, 10);
        const t = byTech.get(e.empno) ?? {
          empno: e.empno,
          name: names.get(e.empno) ?? e.empno,
          derivedHours: new Map<string, number>(),
          dollars: new Map<string, number>(),
        };
        const hours = (e.reg_hr ?? 0) + (e.ot_hr ?? 0);
        t.derivedHours.set(day, (t.derivedHours.get(day) ?? 0) + hours);
        const n = perInvoiceTechs.get(e.invoice) ?? 1;
        const share = e.credit_pct && e.credit_pct > 0 ? ((s.inv_amount ?? 0) * e.credit_pct) / 100 : (s.inv_amount ?? 0) / n;
        t.dollars.set(day, (t.dollars.get(day) ?? 0) + share);
        byTech.set(e.empno, t);
      }
      const techs = [...byTech.values()].sort((a, b) => a.name.localeCompare(b.name));
      return { techs };
    },
  });
}

function usePayTable() {
  return useQuery({
    queryKey: ["tech_pay_table"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<string, TechPayRow>> => {
      // PAY-ONE-01: the one pay record (tech_pay). base_rate 0 = not set.
      const { data, error } = await supabase.from("tech_pay")
        .select("empno, pay_type, base_rate, burden, rhp_multiplier, phone_stipend");
      if (error) throw error;
      const m = new Map<string, TechPayRow>();
      for (const r of (data ?? []) as { empno: string; pay_type: string; base_rate: number | null; burden: number | null; rhp_multiplier: number | null; phone_stipend: number | null }[]) {
        m.set(r.empno, {
          empno: r.empno,
          hourly: r.pay_type === "hourly" && Number(r.base_rate) > 0 ? Number(r.base_rate) : null,
          burden: r.burden == null ? null : Number(r.burden),
          rhp_multiplier: Number(r.rhp_multiplier ?? 1.75),
          phone_stipend: Number(r.phone_stipend ?? 0),
        });
      }
      return m;
    },
  });
}

function useHoursOverrides(mondayIso: string, sundayIso: string) {
  return useQuery({
    queryKey: ["payroll_hours", mondayIso],
    queryFn: async (): Promise<Map<string, number>> => {
      const { data, error } = await supabase
        .from("pegasus_payroll_hours")
        .select("empno, day, hours")
        .gte("day", mondayIso)
        .lte("day", sundayIso);
      if (error) throw error;
      const m = new Map<string, number>();
      for (const r of (data ?? []) as { empno: string; day: string; hours: number }[]) m.set(`${r.empno}|${r.day.slice(0, 10)}`, Number(r.hours));
      return m;
    },
  });
}

function useSaveHours(mondayIso: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { empno: string; day: string; hours: number }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_payroll_hours").upsert(
        { empno: v.empno, day: v.day, hours: v.hours, updated_at: new Date().toISOString(), updated_by: u?.user?.email ?? null },
        { onConflict: "empno,day" },
      );
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payroll_hours", mondayIso] }),
    onError: onErrToast("Couldn't save the hours"),
  });
}

export function PayWeekEngine() {
  const [monday, setMonday] = useState<Date>(() => lastMonday(1));
  const mondayIso = isoOf(monday);
  const sundayIso = isoOf(addDays(monday, 6));
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => isoOf(addDays(monday, i))), [monday]);

  const { data, isLoading } = usePayWeek(mondayIso, sundayIso);
  const { data: payTable } = usePayTable();
  const { data: overrides } = useHoursOverrides(mondayIso, sundayIso);
  const save = useSaveHours(mondayIso);
  const canPay = useCan("team.pay");
  // ?open=rates — Settings → Pay Rates lands straight on the rates.
  const [params] = useSearchParams();
  const [ratesOpen, setRatesOpen] = useState(() => params.get("open") === "rates");

  const threshold = useOrgSettingNumber("payroll_bonus_threshold_pct", 80).data ?? 80;
  const bonusPct = useOrgSettingNumber("payroll_bonus_pct", 3).data ?? 3;
  const holidays = useOrgSettingJson<string[]>("payroll_holidays", []).data ?? [];
  // Holly's daily spiff tiers — highest band that the day's dollars clear wins.
  const spiffTiers = useOrgSettingJson<Array<{ min: number; pct: number }>>(
    "payroll_daily_spiff_tiers",
    [{ min: 3500, pct: 3 }, { min: 2500, pct: 2 }, { min: 1500, pct: 1 }],
  ).data ?? [{ min: 3500, pct: 3 }, { min: 2500, pct: 2 }, { min: 1500, pct: 1 }];
  const spiffPctFor = (dayDollars: number): number =>
    [...spiffTiers].sort((x, y) => y.min - x.min).find((t) => dayDollars >= t.min)?.pct ?? 0;
  const holidaySet = new Set(holidays);

  const techs = data?.techs ?? [];
  const hoursFor = (empno: string, day: string): number =>
    overrides?.get(`${empno}|${day}`) ?? Math.round(((techs.find((t) => t.empno === empno)?.derivedHours.get(day) ?? 0)) * 100) / 100;

  interface Calc {
    t: WeekTech;
    weekHours: number;
    weekDollars: number;
    perHour: number | null;
    rhp: number | null;
    pct: number | null;
    bonus: number;
    spiff: number;
    holHours: number;
  }
  const calcs: Calc[] = techs.map((t) => {
    let weekHours = 0, weekDollars = 0, holHours = 0, spiff = 0;
    for (const d of days) {
      const h = hoursFor(t.empno, d);
      weekHours += h;
      const dd = t.dollars.get(d) ?? 0;
      weekDollars += dd;
      // Daily spiff: the day's whole ticket $ x its band's %.
      const sp = spiffPctFor(dd);
      if (sp > 0) spiff += (dd * sp) / 100;
      if (holidaySet.has(d)) holHours += h;
    }
    const pay = payTable?.get(t.empno);
    const rhp = pay?.hourly != null && pay?.burden != null ? (Number(pay.hourly) + Number(pay.burden)) * Number(pay.rhp_multiplier ?? 1.75) : null;
    const perHour = weekHours > 0 ? weekDollars / weekHours : null;
    const pct = rhp && perHour != null ? (perHour / rhp) * 100 : null;
    const bonus = pct != null && pct >= threshold ? (weekDollars * bonusPct) / 100 : 0;
    return { t, weekHours: Math.round(weekHours * 100) / 100, weekDollars, perHour, rhp, pct, bonus, spiff: Math.round(spiff * 100) / 100, holHours };
  });

  const weekLabel = `${monday.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${addDays(monday, 6).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  function exportAdp() {
    const rows: (string | number)[][] = calcs.map((c) => {
      const pay = payTable?.get(c.t.empno);
      const nonHol = c.weekHours - c.holHours;
      const reg = Math.min(nonHol, 40);
      const ot = Math.max(0, nonHol - 40);
      return [
        c.t.name,
        c.t.empno,
        Math.round(reg * 100) / 100,
        Math.round(ot * 100) / 100,
        Math.round(c.holHours * 100) / 100,
        "", // VAC — Holly's accrual ledger
        "", // ESST — Holly's accrual ledger
        pay?.phone_stipend ? Number(pay.phone_stipend) : "",
        c.bonus > 0 ? Math.round(c.bonus * 100) / 100 : "",
        c.spiff > 0 ? c.spiff : "",
        c.pct != null ? Math.round(c.pct * 10) / 10 : "",
      ];
    });
    downloadCsv(
      `adp-staging-week-${mondayIso}.csv`,
      ["Tech", "Empno", "HRS-REG", "HRS-OT", "HRS-HOL", "HRS-VAC", "HRS-ESST", "PHONE", "BONUS", "SPIFF", "WEEK %"],
      rows,
    );
  }

  const dayHead = (iso: string) => {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString("en-US", { weekday: "short" });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setMonday(addDays(monday, -7))} title="Previous week"
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="inline-flex min-w-52 items-center justify-center gap-1.5 text-sm font-bold text-slate-900">
          Pay Week · {weekLabel}
          <InfoTip label="How the pay week works">
            Hours are proposed from ESC&apos;s per-ticket tech hours by invoice date; type over a cell to match the time cards, and the
            correction (blue) wins from then on. Ticket $ uses ESC&apos;s credit split, or an even split. VAC and ESST stay with the
            accrual ledgers.
          </InfoTip>
        </span>
        <button type="button" onClick={() => setMonday(addDays(monday, 7))} title="Next week"
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
          <ChevronRight className="h-4 w-4" />
        </button>
        <span className="ml-auto flex items-center gap-2">
          {canPay && (
            <Button variant="secondary" size="sm" onClick={() => setRatesOpen(true)}>
              <Settings2 className="h-3.5 w-3.5" /> Pay Rates
            </Button>
          )}
          <Button size="sm" onClick={exportAdp} disabled={calcs.length === 0}>
            <HandCoins className="h-3.5 w-3.5" /> Export ADP Staging
          </Button>
        </span>
      </div>

      {isLoading ? (
        <Card className="h-48 animate-pulse bg-slate-100" />
      ) : calcs.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500">No invoiced tech work found in this week.</Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[880px] text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-[12px] font-semibold text-slate-500">
                <th className="px-3 py-2">Tech</th>
                {days.map((d) => (
                  <th key={d} className={cn("px-1.5 py-2 text-center", holidaySet.has(d) && "bg-amber-50 text-amber-700")}>
                    {dayHead(d)}
                    {holidaySet.has(d) && <span className="block text-[9px] font-bold">HOL</span>}
                  </th>
                ))}
                <th className="px-2 py-2 text-right">Hrs</th>
                <th className="px-2 py-2 text-right">Ticket $</th>
                <th className="px-2 py-2 text-right">$/Hr</th>
                <th className="px-2 py-2 text-right">RHP</th>
                <th className="px-2 py-2 text-right">
                  <span className="inline-flex items-center gap-1">
                    %
                    <InfoTip align="end" label="How % and Bonus are figured">
                      % = ($ ÷ hours) ÷ RHP, where RHP = (hourly + burden) × {payTable?.size ? "the tech's multiplier" : "1.75"}. Bonus
                      = {bonusPct}% of the week&apos;s ticket $ at {threshold}% or more.
                    </InfoTip>
                  </span>
                </th>
                <th className="px-2 py-2 text-right">
                  <span className="inline-flex items-center gap-1">
                    Spiff
                    <InfoTip align="end" label="How the spiff is figured">
                      Each day whose ticket $ clears a band pays that band&apos;s % of the day&apos;s ticket $:{" "}
                      {[...spiffTiers].sort((x, y) => x.min - y.min).map((t) => `$${t.min.toLocaleString()}+ = ${t.pct}%`).join(" · ")}. Shown in
                      purple under the day.
                    </InfoTip>
                  </span>
                </th>
                <th className="px-3 py-2 text-right">Bonus</th>
              </tr>
            </thead>
            <tbody>
              {calcs.map((c) => (
                <tr key={c.t.empno} className="border-b border-slate-50 align-top">
                  <td className="px-3 py-2">
                    <span className="flex items-center gap-2">
                      <TechAvatar empno={c.t.empno} name={c.t.name} size="sm" />
                      <span className="font-medium leading-tight text-slate-800">{c.t.name}</span>
                    </span>
                  </td>
                  {days.map((d) => {
                    const overridden = overrides?.has(`${c.t.empno}|${d}`) ?? false;
                    const dollars = c.t.dollars.get(d) ?? 0;
                    return (
                      <td key={d} className={cn("px-1 py-1.5 text-center", holidaySet.has(d) && "bg-amber-50/50")}>
                        <input
                          type="number"
                          step="0.25"
                          min="0"
                          defaultValue={hoursFor(c.t.empno, d) || ""}
                          placeholder="—"
                          title={overridden ? "Corrected by the office (saved in Pegasus)" : "Proposed from ESC tickets — type to correct"}
                          onBlur={(e) => {
                            const v = Number(e.target.value || 0);
                            if (Math.abs(v - hoursFor(c.t.empno, d)) > 0.001) save.mutate({ empno: c.t.empno, day: d, hours: v });
                          }}
                          className={cn(
                            "w-14 rounded-md border px-1 py-0.5 text-center text-sm tabular-nums focus:border-brand-400 focus:outline-none",
                            overridden ? "border-brand-300 bg-brand-50/60 font-semibold text-brand-800" : "border-hairline",
                          )}
                        />
                        <span className="mt-0.5 block text-[10px] tabular-nums text-slate-400">{dollars > 0 ? usd(dollars) : ""}</span>
                        {spiffPctFor(dollars) > 0 && (
                          <span className="block text-[9px] font-bold tabular-nums text-violet-600" title={`Day cleared $${[...spiffTiers].sort((x, y) => y.min - x.min).find((tt) => dollars >= tt.min)?.min.toLocaleString()} — ${spiffPctFor(dollars)}% of the day's ticket $`}>
                            +{spiffPctFor(dollars)}% · {usd((dollars * spiffPctFor(dollars)) / 100)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-right font-medium tabular-nums">{c.weekHours || "—"}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{usd(c.weekDollars)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{c.perHour != null ? usd(c.perHour) : "—"}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-slate-500">
                    {c.rhp != null ? usdExact(c.rhp) : <span title="No pay basis yet — set hourly + burden under Pay Rates" className="cursor-help text-amber-600">set rates</span>}
                  </td>
                  <td className={cn("px-2 py-2 text-right font-semibold tabular-nums", c.pct == null ? "text-slate-300" : c.pct >= threshold ? "text-emerald-700" : "text-slate-600")}>
                    {c.pct != null ? `${Math.round(c.pct * 10) / 10}%` : "—"}
                  </td>
                  <td className="px-2 py-2 text-right font-semibold tabular-nums text-violet-700">{c.spiff > 0 ? usdExact(c.spiff) : "—"}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-emerald-700">{c.bonus > 0 ? usdExact(c.bonus) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="border-t border-slate-100 px-4 py-2 text-[11px] leading-snug text-slate-400">
            Proposed hours — type over any cell to correct.
          </p>
        </Card>
      )}

      {ratesOpen && canPay && <PayRatesModal onClose={() => setRatesOpen(false)} techs={techs.map((t) => ({ empno: t.empno, name: t.name }))} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PayRatesModal — the sensitive half (team.pay): hourly + burden + multiplier
// + phone stipend per tech. RHP previews live as she types.
// ---------------------------------------------------------------------------
function PayRatesModal({ onClose, techs }: { onClose: () => void; techs: { empno: string; name: string }[] }) {
  const qc = useQueryClient();
  const { data: payTable } = usePayTable();
  const [drafts, setDrafts] = useState<Record<string, { hourly: string; burden: string; mult: string; phone: string }>>({});

  const draftFor = (empno: string) => {
    const cur = drafts[empno];
    if (cur) return cur;
    const row = payTable?.get(empno);
    return {
      hourly: row?.hourly != null ? String(row.hourly) : "",
      burden: row?.burden != null ? String(row.burden) : "",
      mult: String(row?.rhp_multiplier ?? 1.75),
      phone: row?.phone_stipend ? String(row.phone_stipend) : "",
    };
  };
  const setField = (empno: string, k: "hourly" | "burden" | "mult" | "phone", v: string) =>
    setDrafts((d) => ({ ...d, [empno]: { ...draftFor(empno), [k]: v } }));

  const saveRow = useMutation({
    mutationFn: async (empno: string) => {
      const d = draftFor(empno);
      const { data: u } = await supabase.auth.getUser();
      // PAY-ONE-01: writes the one pay record — only these columns, so the
      // pay type, commission and note set in Team Directory stay as they are.
      const { error } = await supabase.from("tech_pay").upsert(
        {
          empno,
          base_rate: d.hourly.trim() ? Number(d.hourly) : 0,
          burden: d.burden.trim() ? Number(d.burden) : null,
          rhp_multiplier: d.mult.trim() ? Number(d.mult) : 1.75,
          phone_stipend: d.phone.trim() ? Number(d.phone) : 0,
          updated_at: new Date().toISOString(),
          updated_by: u?.user?.email ?? null,
        },
        { onConflict: "empno" },
      );
      if (error) throw error;
    },
    onSuccess: (_d, empno) => Promise.all([
      qc.invalidateQueries({ queryKey: ["tech_pay_table"] }),
      qc.invalidateQueries({ queryKey: ["tech_pay", empno] }),
      qc.invalidateQueries({ queryKey: ["tech_rates"] }),
      qc.invalidateQueries({ queryKey: ["scoreboard_productivity"] }),
    ]),
    onError: onErrToast("Couldn't save the pay basis"),
  });

  const inputCls = "w-20 rounded-md border border-hairline px-1.5 py-1 text-right text-sm tabular-nums focus:border-brand-400 focus:outline-none";
  return (
    <Modal open onClose={onClose} size="lg"
      title={
        <span className="inline-flex items-center gap-1.5">
          Pay Rates
          <InfoTip label="How pay rates work">
            Hourly and burden are dollars per hour, per tech. RHP = (hourly + burden) × multiplier. This is the same pay record as Team
            Directory → a person → Pay &amp; Commission.
          </InfoTip>
        </span>
      }
      subtitle="Private: only people with the pay permission see it.">
      <div className="max-h-[60vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-[12px] font-semibold text-slate-500">
              <th className="py-2 pr-2">Tech</th>
              <th className="px-2 py-2 text-right">Hourly</th>
              <th className="px-2 py-2 text-right">Burden</th>
              <th className="px-2 py-2 text-right">Mult.</th>
              <th className="px-2 py-2 text-right">RHP</th>
              <th className="px-2 py-2 text-right">Phone</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {techs.map((t) => {
              const d = draftFor(t.empno);
              const rhp = d.hourly.trim() && d.burden.trim() ? (Number(d.hourly) + Number(d.burden)) * (Number(d.mult) || 1.75) : null;
              return (
                <tr key={t.empno} className="border-b border-slate-50">
                  <td className="py-1.5 pr-2">
                    <span className="flex items-center gap-2">
                      <TechAvatar empno={t.empno} name={t.name} size="xs" />
                      <span className="font-medium text-slate-800">{t.name}</span>
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-right"><input value={d.hourly} onChange={(e) => setField(t.empno, "hourly", e.target.value)} placeholder="$" className={inputCls} /></td>
                  <td className="px-2 py-1.5 text-right"><input value={d.burden} onChange={(e) => setField(t.empno, "burden", e.target.value)} placeholder="$" className={inputCls} /></td>
                  <td className="px-2 py-1.5 text-right"><input value={d.mult} onChange={(e) => setField(t.empno, "mult", e.target.value)} className={cn(inputCls, "w-14")} /></td>
                  <td className="px-2 py-1.5 text-right font-semibold tabular-nums text-slate-700">{rhp != null ? usdExact(rhp) : "—"}</td>
                  <td className="px-2 py-1.5 text-right"><input value={d.phone} onChange={(e) => setField(t.empno, "phone", e.target.value)} placeholder="$" className={cn(inputCls, "w-16")} /></td>
                  <td className="px-2 py-1.5 text-right">
                    <Button variant="secondary" size="sm" loading={saveRow.isPending} onClick={() => saveRow.mutate(t.empno)}>Save</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex justify-end border-t border-hairline pt-3">
        <Button variant="secondary" size="sm" onClick={onClose}><X className="h-3.5 w-3.5" /> Close</Button>
      </div>
    </Modal>
  );
}
